import { useUpdateSuperAdminShopBabackOptions } from "@babackapp/private-api-client/client/use-super-admin-shop-configuration";
import type { SuperAdminBabackOptionDto } from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Switch } from "@babackapp/ui/components/switch";
import { useState } from "react";
import { ConfigurationSection } from "./shop-account-sections";

type OptionValue = boolean | number | string | null;

const SECTIONS: Array<{
  key: SuperAdminBabackOptionDto["section"];
  title: string;
}> = [
  { key: "returns", title: "Retours" },
  { key: "exchange", title: "Échanges" },
  { key: "discount", title: "Avoirs" },
  { key: "logistician", title: "Logisticien" },
  { key: "portal", title: "Portail client" },
  { key: "internal", title: "Interne" },
];

function parse(json: string | null | undefined): OptionValue {
  if (json === null || json === undefined) return null;
  try {
    return JSON.parse(json) as OptionValue;
  } catch {
    return json;
  }
}

/** The form's value of an option: what is read, else its default. */
function initialValue(option: SuperAdminBabackOptionDto): OptionValue {
  return parse(option.valueJson) ?? parse(option.defaultJson);
}

function OptionControl({
  option,
  value,
  onChange,
}: {
  option: SuperAdminBabackOptionDto;
  value: OptionValue;
  onChange: (value: OptionValue) => void;
}) {
  if (option.kind === "boolean") {
    return (
      <Switch
        checked={value === true}
        onCheckedChange={(checked) => onChange(checked)}
      />
    );
  }
  if (option.kind === "integer") {
    return (
      <Input
        type="number"
        step={1}
        min={option.min ?? undefined}
        max={option.max ?? undefined}
        value={value === null ? "" : String(value)}
        onChange={(event) =>
          onChange(
            event.target.value === "" ? null : Number(event.target.value),
          )
        }
        className="h-8 w-28"
      />
    );
  }
  return (
    <Input
      value={typeof value === "string" ? value : ""}
      maxLength={option.maxLength ?? undefined}
      onChange={(event) => onChange(event.target.value || null)}
      className="h-8 w-80 font-mono text-xs"
    />
  );
}

/**
 * Options the merchant cannot set from Returns or Tracking (BBS-406): only
 * Baback does, here. One field at a time is written, the rest of its block
 * is kept; every change is journaled with its values.
 */
export function ShopBabackOptionsSection({
  shopId,
  options,
}: {
  shopId: string;
  options: SuperAdminBabackOptionDto[];
}) {
  const update = useUpdateSuperAdminShopBabackOptions(shopId);
  const [draft, setDraft] = useState<Record<string, OptionValue>>({});

  const changes = Object.fromEntries(
    Object.entries(draft).filter(([key, value]) => {
      const option = options.find((item) => item.key === key);
      return option && value !== initialValue(option);
    }),
  );
  const changedCount = Object.keys(changes).length;

  function save() {
    update.mutate(
      { params: { path: { shopId } }, body: { changes } },
      { onSuccess: () => setDraft({}) },
    );
  }

  return (
    <ConfigurationSection
      title="Options Baback"
      description="Réglages que le marchand ne peut pas modifier depuis Returns ou Tracking. Chaque changement est tracé dans le journal de la boutique."
    >
      <div className="divide-y">
        {SECTIONS.map(({ key, title }) => {
          const sectionOptions = options.filter(
            (option) => option.section === key,
          );
          if (sectionOptions.length === 0) return null;
          return (
            <div key={key} className="px-5 py-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
              </h3>
              <div className="divide-y">
                {sectionOptions.map((option) => {
                  const value =
                    option.key in draft
                      ? draft[option.key]
                      : initialValue(option);
                  const isChanged = option.key in changes;
                  return (
                    <div
                      key={option.key}
                      className="flex items-center justify-between gap-6 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {option.label}
                          {!option.isStored && !isChanged && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              {option.defaultJson === null
                                ? "non réglé"
                                : "valeur par défaut"}
                            </span>
                          )}
                          {isChanged && (
                            <span className="ml-2 text-xs font-normal text-violet-700">
                              modifié
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {option.description}
                        </p>
                        <p className="font-mono text-[11px] text-muted-foreground">
                          {option.key}
                        </p>
                      </div>
                      <OptionControl
                        option={option}
                        value={value}
                        onChange={(next) =>
                          setDraft((current) => ({
                            ...current,
                            [option.key]: next,
                          }))
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-3 border-t px-5 py-3">
        {update.isError && (
          <span className="text-xs text-destructive">
            Enregistrement refusé : vérifie les valeurs.
          </span>
        )}
        {changedCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setDraft({})}>
            Annuler
          </Button>
        )}
        <Button
          size="sm"
          disabled={changedCount === 0 || update.isPending}
          onClick={save}
        >
          {update.isPending
            ? "Enregistrement…"
            : changedCount > 0
              ? `Enregistrer ${changedCount} option${changedCount > 1 ? "s" : ""}`
              : "Enregistrer"}
        </Button>
      </div>
    </ConfigurationSection>
  );
}
